import { type AnalyticsEventName, type BaseEventParams } from './types';
import { pushToDataLayer } from './gtm';
import { getAttributionParams } from './attribution';

const LINKEDIN_CONVERSIONS: Record<string, string> = {
  checkout_started: '10001',
  subscription_started: '10002',
  payment_completed: '10003',
  signup_completed: '10004',
};

const CLARITY_CUSTOM_EVENTS: AnalyticsEventName[] = [
  'signup_completed',
  'workspace_created',
  'editor_opened',
  'template_created',
  'template_published',
  'subscription_started',
  'onboarding_completed',
  'first_export_completed',
];

/**
 * Strict PII and sensitive data sanitizer.
 * Guarantees NO direct identifiers (email, password, tokens, card details, private text)
 * are pushed to dataLayer or third-party pixels.
 */
function sanitizeParams(params: Record<string, unknown>): Record<string, unknown> {
  const sanitized = { ...params };
  const sensitiveKeys = [
    'email',
    'invited_email',
    'password',
    'password_confirmation',
    'token',
    'secret',
    'apikey',
    'api_key',
    'card_number',
    'cvv',
    'cvc',
    'auth_token',
    'session_token',
    'credit_card',
    'phone',
    'ssn',
    'first_name',
    'last_name',
    'message',
  ];

  for (const [key, val] of Object.entries(sanitized)) {
    if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
      delete sanitized[key];
      continue;
    }
    // Strip values that match email address patterns to prevent accidental PII leakage
    if (typeof val === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) {
      delete sanitized[key];
    }
  }
  return sanitized;
}

function getEnvironment(): 'development' | 'staging' | 'production' {
  const env = process.env.NEXT_PUBLIC_APP_ENV ?? process.env.NODE_ENV;
  if (env === 'development') return 'development';
  if (env === 'staging') return 'staging';
  return 'production';
}

function getEnrichedParams(params?: BaseEventParams): BaseEventParams {
  const enriched: BaseEventParams = { ...params };

  if (typeof window !== 'undefined') {
    if (window.analyticsUserContext) {
      const ctx = window.analyticsUserContext;
      // Only attach non-PII anonymous IDs
      if (ctx.user_id && !enriched.user_id) enriched.user_id = ctx.user_id as string;
      if (ctx.workspace_id && !enriched.workspace_id) enriched.workspace_id = ctx.workspace_id as string;
      if (ctx.plan && !enriched.plan) enriched.plan = ctx.plan as string;
    }

    try {
      const attribution = getAttributionParams();
      if (attribution) {
        if (!enriched.utm_source && attribution.utm_source) enriched.utm_source = attribution.utm_source;
        if (!enriched.utm_medium && attribution.utm_medium) enriched.utm_medium = attribution.utm_medium;
        if (!enriched.utm_campaign && attribution.utm_campaign) enriched.utm_campaign = attribution.utm_campaign;
        if (!enriched.utm_content && attribution.utm_content) enriched.utm_content = attribution.utm_content;
        if (!enriched.utm_term && attribution.utm_term) enriched.utm_term = attribution.utm_term;
        if (!enriched.ref && attribution.ref) enriched.ref = attribution.ref;
      }
    } catch (e) {
      console.error('[Analytics] Failed to merge stored UTMs:', e);
    }

    if (!enriched.page_path) enriched.page_path = window.location.pathname;
    if (!enriched.page_url) enriched.page_url = window.location.href;
    if (!enriched.page_title) enriched.page_title = document.title;
    if (!enriched.referrer) enriched.referrer = document.referrer || undefined;
  }

  if (!enriched.environment) {
    enriched.environment = getEnvironment();
  }

  if (!enriched.timestamp) {
    enriched.timestamp = Date.now();
  }

  return sanitizeParams(enriched) as BaseEventParams;
}

export const trackEvent = (eventName: AnalyticsEventName, params?: BaseEventParams) => {
  const enrichedParams = getEnrichedParams(params);

  pushToDataLayer({
    event: eventName,
    event_name: eventName,
    ...enrichedParams,
  });

  if (typeof window === 'undefined') return;

  if (CLARITY_CUSTOM_EVENTS.includes(eventName)) {
    void import('./clarity').then(({ trackClarityEvent }) => {
      trackClarityEvent(eventName);
    });
  }

  if (window.fbq) {
    try {
      if (eventName === 'checkout_started') {
        window.fbq('track', 'InitiateCheckout', {
          content_category: 'subscription',
          ...enrichedParams,
        });
      } else if (eventName === 'payment_completed') {
        window.fbq('track', 'Purchase', {
          value: enrichedParams.amount ?? 0,
          currency: enrichedParams.currency ?? 'USD',
          ...enrichedParams,
        });
      } else if (eventName === 'signup_completed') {
        window.fbq('track', 'CompleteRegistration', enrichedParams);
      } else {
        window.fbq('trackCustom', eventName, enrichedParams);
      }
    } catch (err) {
      console.error('[Analytics] Meta tracking error:', err);
    }
  }

  if (window.lintrk) {
    try {
      const conversionId = LINKEDIN_CONVERSIONS[eventName];
      if (conversionId) {
        window.lintrk('track', { conversion_id: conversionId });
      }
    } catch (err) {
      console.error('[Analytics] LinkedIn tracking error:', err);
    }
  }
};

export const trackPageView = (url: string, title?: string) => {
  const pageTitle = title || (typeof document !== 'undefined' ? document.title : '');
  const enriched = getEnrichedParams({
    page_path: url,
    page_title: pageTitle,
    page_url: typeof window !== 'undefined' ? window.location.href : url,
  });

  pushToDataLayer({
    event: 'page_view',
    ...enriched,
  });

  if (typeof window === 'undefined') return;

  if (window.fbq) {
    try {
      window.fbq('track', 'PageView');
    } catch (err) {
      console.error('[Analytics] Meta PageView error:', err);
    }
  }
};
